import React from "react";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { MapPin, Mountain, Calendar, Sparkles } from "lucide-react";
import CoffeeBeltMap from "@/components/CoffeeBeltMap";
import Seo from "@/components/Seo";

export default function Countries() {
  const { t, lang, isRTL } = useI18n();
  const { data: countries = [], isLoading } = useQuery({ queryKey: ["countries"], queryFn: endpoints.countries });
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-24" data-testid="countries-page">
      <Seo
        title={isRTL ? "أصول القهوة حول العالم — دول إنتاج البن" : "Coffee Origins — Producing Countries"}
        description={isRTL
          ? "خريطة تفاعلية لدول إنتاج القهوة المختصة: إثيوبيا، كولومبيا، اليمن، البرازيل، كينيا، باناما وغيرها — مع ملاحظات النكهة، الارتفاع، والأصناف."
          : "Interactive coffee belt map: Ethiopia, Colombia, Yemen, Brazil, Kenya, Panama and more — with flavor notes, altitude, and cultivars."}
        keywords="أصول القهوة, دول إنتاج القهوة, قهوة إثيوبية, قهوة يمنية, قهوة كولومبية, حزام القهوة, coffee belt, coffee origins, coffee terroir"
        path="/countries"
      />
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-4">
          {isRTL ? `${countries.length || ""} أصول` : `${countries.length || ""} origins`}
        </div>
        <h1 className={`${serifClass} text-4xl lg:text-6xl text-[#2A2421] leading-[1.02]`}>{t.countries.title}</h1>
        <p className="mt-6 text-lg text-[#5C544F] leading-relaxed">{t.countries.lead}</p>
      </div>

      {/* Coffee Belt — real coffee-producing countries map with EN/AR labels */}
      <div className="mt-12">
        <CoffeeBeltMap caption={isRTL ? "حزام القهوة" : "Coffee Belt"} isRTL={isRTL} />
      </div>

      {isLoading ? (
        <div className="mt-16 text-[#5C544F]" data-testid="countries-loading">{t.common.loading}</div>
      ) : (
        <div className="mt-16 grid md:grid-cols-2 gap-6" data-testid="countries-grid">
          {countries.map((c, idx) => (
            <article
              key={c.code}
              className="soft-card rounded-2xl p-7 hover:-translate-y-0.5 transition-transform"
              data-testid={`country-card-${c.code}`}
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#4A5D4E]">
                    <MapPin className="w-3.5 h-3.5" /> {c.region}
                  </div>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    {c.flag && (
                      <span
                        className="text-4xl leading-none drop-shadow-sm"
                        aria-hidden="true"
                        data-testid={`country-flag-${c.code}`}
                      >
                        {c.flag}
                      </span>
                    )}
                    <h2 className={`${serifClass} text-3xl lg:text-4xl text-[#2A2421]`}>
                      {lang === "ar" ? c.name_ar : c.name_en}
                    </h2>
                  </div>
                </div>
                <div className="text-5xl font-display-serif text-[#C86240] opacity-60 flex-shrink-0">{String(idx + 1).padStart(2, "0")}</div>
              </div>

              <p className="text-[#5C544F] leading-relaxed mb-6">
                {lang === "ar" ? c.description_ar : c.description_en}
              </p>

              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <Info label={t.countries.altitude} icon={Mountain} value={c.altitude} />
                <Info label={t.countries.harvest} icon={Calendar} value={c.harvest} />
                <Info label={t.countries.process} icon={Sparkles} value={c.process.join(", ")} />
                <Info label={t.countries.varieties} icon={Sparkles} value={c.varieties.join(", ")} />
              </dl>

              <div className="mt-6 pt-6 border-t border-[#2A2421]/10">
                <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-2">{t.countries.flavor}</div>
                <p className={`${serifClass} text-lg text-[#2A2421] leading-relaxed`}>
                  {lang === "ar" ? c.flavor_ar : c.flavor_en}
                </p>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

const Info = ({ label, icon: Icon, value }) => (
  <div>
    <dt className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#5C544F] mb-1">
      <Icon className="w-3 h-3" /> {label}
    </dt>
    <dd className="text-[#2A2421]">{value}</dd>
  </div>
);
